import { Close } from '@mui/icons-material';
import {
  AccountBalance,
  Assignment,
  BusinessCenter,
  CalendarToday,
  Description,
  MonetizationOn,
  Person,
  Schedule,
  Visibility,
  Web,
} from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';
import { hideTenderModal } from 'services/redux/tenderModalSlice';

import { getTenderFieldTranslation } from '../../../consts/tenderTranslations';
import {
  formatDate,
  formatMoney,
  parseRutWithDots,
} from '../../../functions/utils';
import InfoCard from './Components/InfoCard';
import ItemsSection from './Components/ItemsSection';
import SectionCard from './Components/SectionCard';
import TenderHeader from './Components/TenderHeader';

const TenderModal: React.FC = () => {
  const dispatch = useDispatch();
  const { open, tender } = useSelector((state: RootState) => state.tenderModal);
  const profileUrl = useMemo(() => {
    return `https://comprador.mercadopublico.cl/ficha/informacion-general/${parseRutWithDots(tender?.buyerId)}`;
  }, [tender]);

  const handleClose = () => {
    dispatch(hideTenderModal());
  };

  if (!tender) return null;

  const formatFieldValue = (
    field: string,
    value: number | string | null,
  ): string => {
    if (value === null || value === undefined) return 'No disponible';

    if (field === 'amountVisible' || field === 'estimatedAmount') {
      if (typeof value === 'number') return formatMoney(value);
    }

    if (
      field === 'creationDate' ||
      field === 'closeDate' ||
      field === 'initialDate' ||
      field === 'finalDate' ||
      field === 'publicationDate' ||
      field === 'signatureDate' ||
      field === 'adjudicationDate'
    ) {
      if (value) return formatDate(new Date(value), 'dd-mm-yyyy');
    }
    if (typeof value === 'string')
      return getTenderFieldTranslation(field, value);
    return 'No disponible';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
          backgroundColor: 'rgba(0,0,0,0.04)',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.08)',
          },
        }}
      >
        <Close />
      </IconButton>

      <Box sx={{ p: 2.5, pt: 3 }}>
        <TenderHeader
          name={tender.name ?? undefined}
          externalCode={tender.externalCode ?? undefined}
          statusCode={tender.statusCode}
        />

        <Grid container spacing={2}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <SectionCard title="Información General">
              <Grid container spacing={1.5}>
                <InfoCard
                  icon={<Description />}
                  label="Descripción"
                  valueText={formatFieldValue(
                    'description',
                    tender.description,
                  )}
                  color={'brown'}
                  clampLines={3}
                  showTooltip
                />
                <InfoCard
                  icon={<BusinessCenter />}
                  label="Tipo de Licitación"
                  valueText={formatFieldValue('type', tender.type)}
                  color={'primary.main'}
                />
                <InfoCard
                  icon={<MonetizationOn />}
                  label="Moneda"
                  valueText={formatFieldValue('currency', tender.currency)}
                  color={'success.main'}
                />
                <InfoCard
                  icon={<Visibility />}
                  label="Monto Visible"
                  valueText={
                    tender.amountVisible === 1
                      ? 'Sí'
                      : tender.amountVisible === 0
                        ? 'No'
                        : 'No disponible'
                  }
                  color={'info.main'}
                />
                <InfoCard
                  icon={<AccountBalance />}
                  label="Monto Estimado"
                  valueText={formatFieldValue(
                    'estimatedAmount',
                    tender.estimatedAmount,
                  )}
                  color={'warning.main'}
                />
                <InfoCard
                  icon={<MonetizationOn />}
                  label="Modalidad de Pago"
                  valueText={formatFieldValue(
                    'paymentType',
                    tender.paymentType,
                  )}
                  color={'success.main'}
                />
                <InfoCard
                  icon={<Schedule />}
                  label="Tiempo de Evaluación"
                  valueText={formatFieldValue('time', tender.time)}
                  color={'info.main'}
                />
                <InfoCard
                  icon={<Schedule />}
                  label="Duración del Contrato"
                  valueText={formatFieldValue(
                    'contractDuration',
                    tender.contractDuration,
                  )}
                  color={'warning.main'}
                />
                <InfoCard
                  icon={<Person />}
                  label="Número de Oferentes"
                  valueText={formatFieldValue('bidders', tender.bidders)}
                  color={'primary.main'}
                />
              </Grid>
            </SectionCard>

            {/* Dates Information */}
            <SectionCard title="Fechas Importantes" chipLabel="Cronología">
              <Grid container spacing={1.5}>
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Creación"
                  valueText={formatFieldValue(
                    'creationDate',
                    tender.creationDate,
                  )}
                  color={'primary.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Cierre"
                  valueText={formatFieldValue('closeDate', tender.closeDate)}
                  color={'error.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Inicio"
                  valueText={formatFieldValue(
                    'initialDate',
                    tender.initialDate,
                  )}
                  color={'success.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha Final"
                  valueText={formatFieldValue('finalDate', tender.finalDate)}
                  color={'warning.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Publicación"
                  valueText={formatFieldValue(
                    'publicationDate',
                    tender.publicationDate,
                  )}
                  color={'info.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Firma"
                  valueText={formatFieldValue(
                    'signatureDate',
                    tender.signatureDate,
                  )}
                  color={'success.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Adjudicación"
                  valueText={formatFieldValue(
                    'adjudicationDate',
                    tender.adjudicationDate,
                  )}
                  color={'primary.main'}
                />
              </Grid>
            </SectionCard>

            {/* Buyer Information */}
            <SectionCard title="Información del Comprador">
              <Grid container spacing={1.5}>
                <InfoCard
                  icon={<Person />}
                  label="Nombre"
                  valueText={formatFieldValue('name', tender.buyer.name)}
                  color={'primary.main'}
                />
                <InfoCard
                  icon={<Assignment />}
                  label="Identificador"
                  valueText={formatFieldValue(
                    'identifier',
                    tender.buyer.identifier ?? null,
                  )}
                  color={'info.main'}
                />
                <InfoCard
                  icon={<Assignment />}
                  label="ID"
                  valueText={formatFieldValue('id', tender.buyer.id)}
                  color={'warning.main'}
                />
                {profileUrl && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          backgroundColor: 'rgba(3, 169, 244, 0.1)',
                          borderRadius: '50%',
                          p: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Web sx={{ fontSize: '0.8rem', color: 'info.main' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          Ver perfil
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <a
                            href={profileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#1976d2',
                              textDecoration: 'none',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration =
                                'underline';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = 'none';
                            }}
                          >
                            Abrir en nueva pestaña
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ItemsSection items={tender.items} />

            <SectionCard title="Información Adicional">
              <Grid container spacing={1.5}>
                {tender.url && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          backgroundColor: 'rgba(3, 169, 244, 0.1)',
                          borderRadius: '50%',
                          p: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Web sx={{ fontSize: '0.8rem', color: 'info.main' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          Ver Licitación
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <a
                            href={tender.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#1976d2',
                              textDecoration: 'none',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration =
                                'underline';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = 'none';
                            }}
                          >
                            Abrir en nueva pestaña
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                <InfoCard
                  icon={<Person />}
                  label="Oficial de Pago"
                  valueText={formatFieldValue(
                    'paymentOfficer',
                    tender.paymentOfficer,
                  )}
                  color={'success.main'}
                />
                <InfoCard
                  icon={<Person />}
                  label="Oficial de Contacto"
                  valueText={formatFieldValue(
                    'contactOfficer',
                    tender.contactOfficer,
                  )}
                  color={'primary.main'}
                />
                <InfoCard
                  icon={<Assignment />}
                  label="Número de Adjudicación"
                  valueText={formatFieldValue(
                    'adjudicationNumber',
                    tender.adjudicationNumber,
                  )}
                  color={'warning.main'}
                />
              </Grid>
            </SectionCard>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default TenderModal;
